import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';

import {
  createNewAssignment,
  createNewAssignmentFromDraft,
} from './assignmentSlice';
import backofficeApiClient, {
  CreateWorkerModel,
  AssignmentPrimaryDocumentEditModel,
} from 'api/backofficeApiClient';
import adminApiClient from 'api/adminApiClient';
import { logout } from './authenticationSlice';
import { isAnyActionOf } from '../utils';

interface WorkerState {
  isWorkerModalOpen: boolean;
  workerCreatedAt?: number;
  workerDetailsUpdatedAtByAdmin?: number;
}

const initialState: WorkerState = {
  isWorkerModalOpen: false,
  workerCreatedAt: undefined,
  workerDetailsUpdatedAtByAdmin: undefined,
};

interface UpdateWorkerModel {
  data: CreateWorkerModel;
  workerId: string;
  isAdmin: boolean;
}

const updateWorker = createAsyncThunk(
  'worker/updateWorker',
  async (
    { data, workerId, isAdmin }: UpdateWorkerModel,
    { rejectWithValue },
  ) => {
    try {
      const response = isAdmin
        ? await adminApiClient.updateWorker(workerId, data)
        : await backofficeApiClient.updateWorker(workerId, data);

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const createWorker = createAsyncThunk(
  'worker/createWorker',
  async (data: CreateWorkerModel, { rejectWithValue }) => {
    try {
      const response = await backofficeApiClient.createNewWorker(data);

      return response;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const getDuplicatesWorkers = createAsyncThunk(
  'worker/getDuplicatesWorkers',
  async ({ pageNumber, searchQuery }: WorkersRequestModel) => {
    return adminApiClient.getDuplicatesWorkersPaged(searchQuery, pageNumber);
  },
);

interface WorkersRequestModel {
  pageNumber: number;
  searchQuery: string;
  requestId: number;
}

const getApprovedWorkers = createAsyncThunk(
  'worker/getApprovedWorkers',
  async ({ pageNumber, searchQuery }: WorkersRequestModel) => {
    return adminApiClient.getApprovedWorkersPaged(searchQuery, pageNumber);
  },
);

const getWorkersForCurrentMember = createAsyncThunk(
  'worker/getWorkersForCurrentMember',
  async ({ pageNumber, searchQuery }: WorkersRequestModel) => {
    return backofficeApiClient.getPagedWorkersForCurrentMember(
      searchQuery,
      pageNumber,
    );
  },
);

interface WorkerDetailModel {
  workerKey: string;
  isAdminRole?: boolean;
}

const getWorkerDetail = createAsyncThunk(
  'worker/getWorkerDetail',
  async ({ workerKey, isAdminRole }: WorkerDetailModel) => {
    return isAdminRole
      ? adminApiClient.getWorkerDetailByKey(workerKey)
      : backofficeApiClient.getWorkerDetailByKey(workerKey);
  },
);

interface CompanyDetailModel {
  companyId: string;
  isAdminRole?: boolean;
}

const getCompanyDetail = createAsyncThunk(
  'worker/getCompanyDetail',
  async ({ companyId, isAdminRole }: CompanyDetailModel) => {
    return isAdminRole
      ? adminApiClient.getContractorCompanyDetailById(companyId)
      : backofficeApiClient.getContractorCompanyDetailById(companyId);
  },
);

interface DownloadWorkerFileRequest {
  workerId: string;
  isAdminRole: boolean;
}
const getWorkerInfoFile = createAsyncThunk(
  'worker/getWorkerInfoFile',
  async ({ workerId, isAdminRole }: DownloadWorkerFileRequest) => {
    return isAdminRole
      ? adminApiClient.getWorkerFile(workerId)
      : backofficeApiClient.getWorkerFile(workerId);
  },
);

interface MergeWorkersModel {
  workerId: string;
  originalWorkerId: string;
}

const mergeWorker = createAsyncThunk(
  'worker/mergeWorker',
  async ({ workerId, originalWorkerId }: MergeWorkersModel) => {
    return adminApiClient.mergeWorker(workerId, originalWorkerId);
  },
);

const saveWorkerAsSeparate = createAsyncThunk(
  'worker/saveWorkerAsSeparate',
  async (workerId: string) => {
    return adminApiClient.saveWorkerAsSeparate(workerId);
  },
);

interface MemberWorkersRequestModel {
  isAdmin?: boolean;
  companyId: string;
}
const getMemberWorkers = createAsyncThunk(
  'worker/getMemberWorkers',
  async ({ isAdmin, companyId }: MemberWorkersRequestModel) => {
    return isAdmin
      ? adminApiClient.getWorkersForMember(companyId)
      : backofficeApiClient.getWorkersForCurrentMember();
  },
);

const isWorkerCreatedFromAssignment = (
  assignmentData: AssignmentPrimaryDocumentEditModel,
): boolean => {
  const { workerInfo } = assignmentData;

  return !isEmpty(workerInfo) && !workerInfo?.workerId;
};

interface HistoryForWorkerModel {
  entityId: string;
  pageNumber: number;
}
const getHistoryDataForWorker = createAsyncThunk(
  'worker/getHistoryDataForWorker',
  async ({ entityId, pageNumber }: HistoryForWorkerModel) => {
    return adminApiClient.getWorkerHistory(entityId, 20, pageNumber);
  },
);

interface HistoryFileRequestModel {
  workerId?: string;
  fileKey?: string;
}
const getWorkerHistoryFile = createAsyncThunk(
  'worker/getWorkerHistoryFile',
  async ({ workerId, fileKey }: HistoryFileRequestModel) => {
    return adminApiClient.getHistoryWorkerFile(workerId, fileKey);
  },
);

const workerSlice = createSlice({
  name: 'worker',
  initialState,
  reducers: {
    handleWorkerModalOpen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isWorkerModalOpen: action.payload,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      Object.assign(state, initialState);
    });
    builder.addCase(createWorker.fulfilled, (state) => {
      state.workerCreatedAt = Date.now();
    });
    builder.addCase(createNewAssignment.fulfilled, (state, action) => {
      const {
        meta: { arg },
      } = action;

      isWorkerCreatedFromAssignment(arg) &&
        (state.workerCreatedAt = Date.now());
    });
    builder.addCase(createNewAssignmentFromDraft.fulfilled, (state, action) => {
      const {
        meta: { arg },
      } = action;

      isWorkerCreatedFromAssignment(arg.data) &&
        (state.workerCreatedAt = Date.now());
    });
    builder.addMatcher(
      isAnyActionOf(
        // Reload history section on worker's sections updates
        updateWorker.fulfilled,
      ),
      (state) => {
        state.workerDetailsUpdatedAtByAdmin = Date.now();
      },
    );
  },
});

const { reducer, actions } = workerSlice;
const { handleWorkerModalOpen } = actions;

export {
  getDuplicatesWorkers,
  getWorkerDetail,
  mergeWorker,
  saveWorkerAsSeparate,
  getApprovedWorkers,
  getWorkersForCurrentMember,
  getMemberWorkers,
  updateWorker,
  createWorker,
  handleWorkerModalOpen,
  getWorkerInfoFile,
  getHistoryDataForWorker,
  getWorkerHistoryFile,
  getCompanyDetail,
};

export default reducer;
